import { createSelector } from 'reselect';
import { array, nonEmptyArray, option, ord, record, string } from 'fp-ts';
import { pipe, flow } from 'fp-ts/function';
import { ApplicationState, OrderItem } from '@mews-commander/types';
import { Selector } from '@mews/framework';
import { getStringArrayProp, primitiveTypeUniq } from '@mews/fp';
import {
    getReservationsAdditionalExpenses,
    getReservationsAllDefaultProductOrderItems,
    getReservationsDefaultProductOrderItems,
    getReservationsNonDefaultNonDeletedProductOrderItems,
} from '@mews-commander/legacy-entities/src/selectors/orderItems';

const byDiscriminator = ord.contramap((orderItem: OrderItem) => orderItem.data.discriminator)(string.Ord);
const byStartUtc = ord.contramap((orderItem: OrderItem) => orderItem.startUtc ?? '')(string.Ord);
const sortOrderItems = array.sortBy([byDiscriminator, byStartUtc]);

const getSortedDefaultProductOrderItemsFactory = (
    orderItemsSelector: Selector<ApplicationState, { serviceOrderIds: string[]; serviceId: string }, OrderItem[]>
) => createSelector(orderItemsSelector, array.sort(byStartUtc));
export const getSortedActiveDefaultProductOrderItems = getSortedDefaultProductOrderItemsFactory(
    getReservationsDefaultProductOrderItems
);
export const getSortedAllDefaultProductOrderItems = getSortedDefaultProductOrderItemsFactory(
    getReservationsAllDefaultProductOrderItems
);

export const getSortedReservationsAdditionalExpenses = createSelector(
    getReservationsAdditionalExpenses,
    sortOrderItems
);

export const getSortedNonCanceledReservationsAdditionalExpenses = createSelector(
    getReservationsAdditionalExpenses,
    flow(
        array.filter((orderItem) => !orderItem.isCanceled),
        sortOrderItems
    )
);

export const getReservationsCommonProductIds = createSelector(
    getReservationsNonDefaultNonDeletedProductOrderItems,
    getStringArrayProp('serviceOrderIds'),
    (productOrderItems, checkedReservationIds) => {
        if (array.isEmpty(productOrderItems)) {
            return [];
        }

        const checkedReservationIdsCount = array.size(checkedReservationIds);
        if (checkedReservationIdsCount <= 1) {
            return pipe(
                productOrderItems,
                array.filterMap((productOrderItem) => option.fromNullable(productOrderItem.data.value.productId)),
                primitiveTypeUniq
            );
        }

        return pipe(
            productOrderItems,
            array.filter((orderItem) => !!orderItem.data.value.productId),
            nonEmptyArray.groupBy((orderItem) => orderItem.data.value.productId as string),
            record.reduceWithIndex([], (productId, acc: Array<string>, sameProductOrderItems) => {
                const productIsCommon = pipe(
                    sameProductOrderItems,
                    array.map((orderItem) => orderItem.serviceOrderId),
                    primitiveTypeUniq,
                    (serviceOrderIds) => array.size(serviceOrderIds) === checkedReservationIdsCount
                );

                if (productIsCommon) {
                    return [...acc, productId];
                }

                return acc;
            })
        );
    }
);
